import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import SupabaseAuthProvider from './SupabaseAuthProvider';
import reportWebVitals from './reportWebVitals';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import LoginPage from './LoginPage';
import SignUpPage from './SignUpPage';
import CustomLoginPage from './pages/CustomLoginPage';
import CustomSignUpPage from './pages/CustomSignUpPage';
import UnifiedAuthPage from './pages/UnifiedAuthPage';
import ReviewPage from './reviewpage';
import AnonymousReviewFlow from './components/anonymous/AnonymousReviewFlow';
// import { Databuddy } from '@databuddy/sdk';
import { checkClerkConfig, getClerkPublishableKey } from './lib/clerkConfig';

// Check Clerk configuration on app start
checkClerkConfig();

const clerkPubKey = getClerkPublishableKey();

const ReviewPageWrapper: React.FC = () => {
  const { reviewLink } = useParams<{ reviewLink: string }>();
  return <ReviewPage reviewLink={reviewLink || ''} />;
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
// Error fallback component
const ClerkConfigError: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Configuration Error</h3>
        <p className="text-sm text-gray-500 mb-4">
          Clerk authentication is not properly configured. Please check your environment variables.
        </p>
        <div className="bg-gray-100 rounded-md p-3 text-left">
          <p className="text-xs text-gray-600 font-mono">
            Missing: REACT_APP_CLERK_PUBLISHABLE_KEY
          </p>
        </div>
      </div>
    </div>
  </div>
);

root.render(
  <React.StrictMode>
    {!clerkPubKey || clerkPubKey === 'your_clerk_publishable_key_here' ? (
      <ClerkConfigError />
    ) : (
      <ClerkProvider publishableKey={clerkPubKey}>
        <SupabaseAuthProvider>
          {/* <Databuddy
            clientId="_jYQp0W-8EnXK5f-xAC12"
            enableBatching={true}
          /> */}
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <ClerkRoutes />
          </BrowserRouter>
        </SupabaseAuthProvider>
      </ClerkProvider>
    )}
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Component that decides which routes to render based on authentication state
function ClerkRoutes() {
  const { isSignedIn, isLoaded } = useAuth();
  // Wait until Clerk finishes loading to avoid flicker
  if (!isLoaded) return null;

  return (
    <Routes>
      {/* Publicly accessible routes - these should always be available */}
      <Route path="/review/:reviewLink" element={<ReviewPageWrapper />} />
      
      {/* Anonymous review routes - redirect authenticated users to dashboard */}
      <Route 
        path="/anonymous-review" 
        element={
          isSignedIn ? <Navigate to="/" replace /> : <AnonymousReviewFlow />
        } 
      />
      <Route 
        path="/anonymous-review/*" 
        element={
          isSignedIn ? <Navigate to="/" replace /> : <AnonymousReviewFlow />
        } 
      />
      
      {/* Auth pages */}
      <Route path="/auth/*" element={<UnifiedAuthPage />} />
      <Route path="/login/*" element={<CustomLoginPage />} />
      <Route path="/sign-up/*" element={<CustomSignUpPage />} />
      
      {/* Authenticated routes - only available when signed in */}
      {isSignedIn ? (
        <Route path="/*" element={<App />} />
      ) : (
        /* Redirect to auth for any other routes when not signed in */
        <Route path="*" element={<Navigate to="/auth" replace />} />
      )}
    </Routes>
  );
}
